import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Camera, XCircle, ScanBarcode, CheckCircle2, AlertTriangle, Zap } from "lucide-react";
import { toast } from "sonner";

const SCANNER_ELEMENT_ID = "jc-barcode-scanner";

// Short beep using the Web Audio API — no audio file required.
let sharedAudioCtx = null;
const playBeep = (variant = "ok") => {
  try {
    if (!sharedAudioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      sharedAudioCtx = new Ctx();
    }
    const ctx = sharedAudioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = variant === "warn" ? 440 : 880;
    gain.gain.value = 0.001;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    gain.gain.exponentialRampToValueAtTime(0.25, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
    osc.start(now);
    osc.stop(now + 0.16);
  } catch {
    /* silent */
  }
};

const vibrate = (pattern) => {
  try {
    if (navigator.vibrate) navigator.vibrate(pattern);
  } catch {
    /* silent */
  }
};

/**
 * BarcodeScannerDialog — reads QR + common 1D barcodes.
 *
 * Props:
 *   - onDetected(code): called with the scanned code (upper-cased, trimmed).
 *   - continuous: if true, dialog stays open after each scan and keeps the
 *     camera running for rapid bulk scanning. Duplicate scans within 1.5s of
 *     the same code are ignored to avoid double-counting.
 *   - onDetectionResult({ code, ok, message }): optional callback to feed the
 *     in-dialog scan log with parent-side outcomes.
 */
export default function BarcodeScannerDialog({
  open,
  onOpenChange,
  onDetected,
  continuous = false,
  title = "Scan barcode",
}) {
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ code: "", at: 0 });
  const onDetectedRef = useRef(onDetected);
  const onOpenChangeRef = useRef(onOpenChange);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  const [history, setHistory] = useState([]); // [{ code, at, status }]

  useEffect(() => {
    onDetectedRef.current = onDetected;
    onOpenChangeRef.current = onOpenChange;
  }, [onDetected, onOpenChange]);

  useEffect(() => {
    if (!open) {
      setError("");
      setManual("");
      setHistory([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const inst = new Html5Qrcode(SCANNER_ELEMENT_ID, { verbose: false });
        scannerRef.current = inst;
        await inst.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 160 }, aspectRatio: 1.7 },
          (decodedText) => {
            if (cancelled) return;
            const code = String(decodedText || "").trim().toUpperCase();
            if (!code) return;
            const now = Date.now();
            // De-duplicate identical scans within 1.5s
            if (
              lastScanRef.current.code === code &&
              now - lastScanRef.current.at < 1500
            ) {
              return;
            }
            lastScanRef.current = { code, at: now };
            playBeep("ok");
            vibrate([80]);
            setHistory((h) => [{ code, at: now, status: "sent" }, ...h].slice(0, 12));
            onDetectedRef.current?.(code);
            if (!continuous) {
              cleanup();
              onOpenChangeRef.current?.(false);
            }
          },
          () => {},
        );
        if (!cancelled) setRunning(true);
      } catch (err) {
        setError(err?.message || "Camera not available. You can type the code instead.");
      }
    })();
    const cleanup = async () => {
      try {
        if (scannerRef.current) {
          await scannerRef.current.stop();
          scannerRef.current.clear();
        }
      } catch (_) {}
      scannerRef.current = null;
      setRunning(false);
    };
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [open, continuous]);

  const submitManual = (e) => {
    e.preventDefault();
    const code = manual.trim().toUpperCase();
    if (!code) return toast.error("Enter a code");
    playBeep("ok");
    vibrate([80]);
    setHistory((h) => [{ code, at: Date.now(), status: "sent" }, ...h].slice(0, 12));
    onDetectedRef.current?.(code);
    setManual("");
    if (!continuous) onOpenChangeRef.current?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-lg" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <ScanBarcode size={22} /> {title}
            {continuous && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm bg-blue-100 text-blue-800">
                <Zap size={10} /> Bulk mode
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative rounded-sm overflow-hidden border-2 border-dashed border-slate-300 bg-black aspect-video">
            <div id={SCANNER_ELEMENT_ID} className="absolute inset-0" />
            {!running && !error && (
              <div className="absolute inset-0 grid place-items-center text-white/80 text-sm">
                <Camera size={40} />
              </div>
            )}
            {error && (
              <div className="absolute inset-0 grid place-items-center p-6 text-center text-white bg-black/70">
                <div>
                  <XCircle size={32} className="mx-auto text-red-400" />
                  <p className="mt-2 text-sm">{error}</p>
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
              Or type / paste code
            </div>
            <form onSubmit={submitManual} className="flex gap-2">
              <Input
                autoFocus
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                data-testid="barcode-manual-input"
                placeholder="e.g. OS105A"
                className="rounded-sm font-mono uppercase"
              />
              <Button type="submit" data-testid="barcode-manual-submit" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
                Use code
              </Button>
            </form>
            <p className="mt-2 text-xs text-slate-500">
              {continuous
                ? "Keep pointing at boxes — the camera stays on. Beeps and buzzes confirm each scan."
                : "Point at any QR or 1D barcode on the box. We match on SKU / box number."}
            </p>
          </div>

          {continuous && history.length > 0 && (
            <div className="border border-slate-200 rounded-sm max-h-40 overflow-y-auto" data-testid="scan-history">
              <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center justify-between">
                <span>Recent scans</span>
                <span className="font-mono">{history.length}</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {history.map((h, i) => (
                  <li key={`${h.at}-${i}`} className="px-3 py-1.5 flex items-center justify-between text-xs">
                    <span className="font-mono font-semibold text-slate-900">{h.code}</span>
                    <span className="inline-flex items-center gap-1 text-slate-500">
                      {h.status === "unexpected" ? (
                        <><AlertTriangle size={11} className="text-amber-600" /> flagged</>
                      ) : h.status === "error" ? (
                        <><XCircle size={11} className="text-red-500" /> {h.message || "error"}</>
                      ) : (
                        <><CheckCircle2 size={11} className="text-emerald-600" /> received</>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="barcode-close-btn">
            {continuous ? "Done" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
